import { SignInForm } from "@/components/auth/sign-in-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { trackEvent } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";
import { useIsClient } from "@/lib/hooks/useIsClient";
import { cloneElement, useEffect, useState } from "react";
type SignInDialogProps = {
  children: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  analyticsFrom: string;
};
export function SignInDialog({
  open,
  setOpen,
  children,
  analyticsFrom,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  children?: React.ReactNode;
  analyticsFrom: string;
}) {
  useEffect(() => {
    if (open) {
      trackEvent("sign-in-dialog-open", {
        from: analyticsFrom,
      });
    }
  }, [open, analyticsFrom]);
  return (
    <Dialog
      open={open}
      onOpenChange={(newOpenState) => {
        setOpen(newOpenState);
      }}
    >
      {children}
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Sign in</DialogTitle>
          <DialogDescription>
            Sign in or create a free account to save custom ingredients and
            recipes.
          </DialogDescription>
        </DialogHeader>
        <SignInForm
          onSuccess={() => {
            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
export function SignInDialogWithTrigger({
  children,
  analyticsFrom,
}: SignInDialogProps) {
  const [open, setOpen] = useState(false);
  const isClient = useIsClient();
  const { data: session } = useSession();
  // To avoid hydration errors, always render the dialog on first go
  if (isClient && session) {
    return children;
  }
  // The wrapped button behavior should be completely overridden when not signed in
  const trigger = cloneElement(children, {
    onClick: (e) => {
      e.preventDefault();
      setOpen(true);
    },
  });
  return (
    <SignInDialog open={open} setOpen={setOpen} analyticsFrom={analyticsFrom}>
      <DialogTrigger render={trigger}></DialogTrigger>
    </SignInDialog>
  );
}
